import React, { useState, useEffect } from 'react';
import { pipelineService, Pipeline } from '../services/pipelineService';
import { apiService, DeployedAPI } from '../services/apiService';
import TestCommandModal from '../components/api/TestCommandModal';
import '../styles/dashboard-pages.css';

const APIPage: React.FC = () => {
  const [savedPipelines, setSavedPipelines] = useState<Pipeline[]>([]);
  const [deployedAPIs, setDeployedAPIs] = useState<DeployedAPI[]>([]);
  const [loading, setLoading] = useState(false);
  const [deployingPipelineId, setDeployingPipelineId] = useState<string | null>(null);
  const [testCommandApi, setTestCommandApi] = useState<DeployedAPI | null>(null);
  const [showApiKey, setShowApiKey] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pipelines, apis] = await Promise.all([
        pipelineService.listPipelines(),
        apiService.listDeployedAPIs()
      ]);
      setSavedPipelines(pipelines);
      setDeployedAPIs(apis);
    } catch (error: any) {
      console.error('Error loading data:', error);
      alert(`Failed to load data: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeploy = async (pipelineId: string) => {
    setDeployingPipelineId(pipelineId);
    try {
      const result = await apiService.deployPipeline(pipelineId);
      alert(`Pipeline deployed successfully!\nAPI URL: ${result.api_url}\nAPI Key: ${result.api_key}`);
      await loadData(); // Reload to show new API
    } catch (error: any) {
      alert(`Failed to deploy pipeline: ${error.message || 'Unknown error'}`);
    } finally {
      setDeployingPipelineId(null);
    }
  };

  const handleUndeploy = async (apiConfigId: string) => {
    if (!window.confirm('Are you sure you want to remove this API? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.undeployAPI(apiConfigId);
      await loadData(); // Reload to remove API from list
    } catch (error: any) {
      alert(`Failed to remove API: ${error.message || 'Unknown error'}`);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>API</h1>
        <p>Manage deployed APIs and access documentation</p>
      </div>

      <div className="page-sections">
        {/* Saved Pipelines Section */}
        <section className="section-card">
          <h2>Saved Pipelines</h2>
          {loading && savedPipelines.length === 0 ? (
            <div className="empty-state">
              <p>Loading pipelines...</p>
            </div>
          ) : savedPipelines.length === 0 ? (
            <div className="empty-state">
              <p>No saved pipelines found. Create and save a pipeline in the Pipeline Builder to deploy it here.</p>
            </div>
          ) : (
            <div className="projects-list">
              {savedPipelines.map((pipeline) => (
                <div key={pipeline.id} className="project-item">
                  <div className="project-info">
                    <div className="project-name">{pipeline.display_name || pipeline.name}</div>
                    <div className="project-description">{pipeline.description || 'No description'}</div>
                    <div className="project-meta">
                      Created: {formatDate(pipeline.created_at)}
                    </div>
                  </div>
                  <div className="project-actions">
                    <button
                      className="action-btn primary"
                      onClick={() => handleDeploy(pipeline.id)}
                      disabled={deployingPipelineId === pipeline.id}
                    >
                      {deployingPipelineId === pipeline.id ? 'Deploying...' : '🚀 Deploy'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Deployed APIs Section */}
        <section className="section-card">
          <h2>Deployed APIs</h2>
          {loading && deployedAPIs.length === 0 ? (
            <div className="empty-state">
              <p>Loading deployed APIs...</p>
            </div>
          ) : deployedAPIs.length === 0 ? (
            <div className="empty-state">
              <p>No deployed APIs. Deploy a pipeline to create a live API endpoint.</p>
            </div>
          ) : (
            <div className="api-keys-list">
              {deployedAPIs.map((api) => (
                <div key={api.id} className="api-key-item">
                  <div className="key-info">
                    <div className="key-name">API Endpoint</div>
                    <div className="key-created">Created: {formatDate(api.created_at)}</div>
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                      <strong>URL:</strong> <code>{api.api_url || `${window.location.origin}${api.endpoint_path || api.api_url_path || ''}`}</code>
                    </div>
                  </div>
                  <div className="key-actions">
                    <button
                      className="key-btn"
                      onClick={() => setShowApiKey({ ...showApiKey, [api.id]: !showApiKey[api.id] })}
                    >
                      {showApiKey[api.id] ? '👁️ Hide Key' : '👁️ Show Key'}
                    </button>
                    <button
                      className="key-btn"
                      onClick={() => setTestCommandApi(api)}
                    >
                      📋 Test Command
                    </button>
                    <button
                      className="key-btn danger"
                      onClick={() => handleUndeploy(api.id)}
                    >
                      🗑️ Remove
                    </button>
                  </div>
                  {showApiKey[api.id] && (
                    <div className="key-display" style={{ marginTop: '12px' }}>
                      <code>{api.api_key}</code>
                      <button
                        className="copy-btn"
                        onClick={() => navigator.clipboard.writeText(api.api_key)}
                      >
                        📋 Copy
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Test Command Modal */}
        {testCommandApi && (
          <TestCommandModal
            apiConfig={testCommandApi}
            onClose={() => setTestCommandApi(null)}
          />
        )}

        <section className="section-card">
          <h2>API Documentation</h2>
          <div className="api-docs">
            <div className="doc-section">
              <h3>Base URL</h3>
              <code className="code-block">https://api.neuralaviz.com/v1</code>
            </div>

            <div className="doc-section">
              <h3>Authentication</h3>
              <p>Include your API key in the Authorization header:</p>
              <code className="code-block">Authorization: Bearer YOUR_API_KEY</code>
            </div>

            <div className="doc-section">
              <h3>Endpoints</h3>
              <ul className="endpoints-list">
                <li><code>GET /models</code> - List all models</li>
                <li><code>POST /models</code> - Create a new model</li>
                <li><code>GET /models/:id</code> - Get model details</li>
                <li><code>POST /training/start</code> - Start training</li>
                <li><code>GET /training/status/:id</code> - Get training status</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="section-card">
          <h2>Quick Links</h2>
          <div className="quick-links">
            <a href="#" className="quick-link">📖 Full Documentation</a>
            <a href="#" className="quick-link">🐍 Python SDK</a>
            <a href="#" className="quick-link">📦 JavaScript SDK</a>
            <a href="#" className="quick-link">💬 Support</a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default APIPage;
